import { useIntl } from "react-intl";
import { toast } from "react-toastify";
import { UseFormClearErrors, UseFormSetError } from "react-hook-form";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { createNewPaymentLocation, updatePaymentLocation } from "api";
import errorToast from "helpers/errorToast";
import usePermissions from "hooks/usePermissions";
import { userQueryKeys } from "api/queries/user/userQueryKeys";
import { DEFAULT_PAYMENT_LOCATION_VALUES_TYPE } from "../constants";

interface usePaymentLocationMutationsProps {
  refetchPaymentLocations: () => void;
  closeDialog: () => void;
  selectedLocationReferenceNumber?: string;
  viewOnly?: boolean;
  setError: UseFormSetError<DEFAULT_PAYMENT_LOCATION_VALUES_TYPE>;
  clearErrors: UseFormClearErrors<DEFAULT_PAYMENT_LOCATION_VALUES_TYPE>;
}

export default function usePaymentLocationMutations({
  refetchPaymentLocations,
  closeDialog,
  selectedLocationReferenceNumber,
  viewOnly,
  setError,
  clearErrors,
}: usePaymentLocationMutationsProps) {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const { permissions } = usePermissions();
  const { enable_new_address_structure } = JSON.parse(permissions || "{}")?.company_shipper_settings || {};

  const createPayload = (data: any, freightModes: any) => {
    const {
      // Address v1 fields
      country_code,
      city,
      // Address v2 fields
      country_id,
      country_name,
      state_id,
      state_name,
      city_id,
      city_name,
      ...restData
    } = data;

    if (enable_new_address_structure) {
      return {
        ...restData,
        country_id,
        state_id,
        city_id,
        approved_invoice_emails: data?.approved_invoice_emails,
        freight_modes: freightModes,
      };
    }

    return {
      ...restData,
      country_code,
      city,
      approved_invoice_emails: data?.approved_invoice_emails,
      freight_modes: freightModes,
    };
  };

  const createSuccessMessage = intl.formatMessage({
    id: "settings.payment-locations.createSuccess",
    defaultMessage: "Payment location successfully created",
  });

  const updateSuccessMessage = intl.formatMessage({
    id: "settings.payment-locations.updateSuccess",
    defaultMessage: "Payment location successfully updated",
  });

  const { mutate: createPaymentLocationMutation, isLoading: isCreating } = useMutation({
    mutationFn: (payload: any) => createNewPaymentLocation(payload),
    onSuccess: () => {
      toast.success(createSuccessMessage);
      refetchPaymentLocations();
      closeDialog();
    },
    onError: (error: any) => {
      errorToast(error);
    },
  });

  const { mutate: updatePaymentLocationMutation, isLoading: isUpdating } = useMutation({
    mutationFn: (payload: any) => updatePaymentLocation(selectedLocationReferenceNumber, payload),
    onSuccess: () => {
      toast.success(updateSuccessMessage);
      refetchPaymentLocations();
      queryClient.invalidateQueries(userQueryKeys.paymentLocationDetails(selectedLocationReferenceNumber!));
      closeDialog();
    },
    onError: (error: any) => {
      errorToast(error);
    },
  });

  const handleSubmitPaymentLocation = (data: any) => {
    if (viewOnly) return;

    clearErrors();

    const freightModesArray = Object.entries(data.freight_modes || {}).reduce((acc: any, [key, value]: any) => {
      if (value) {
        acc.push(key);
      }
      return acc;
    }, []);

    if (freightModesArray.length === 0) {
      setError("freight_modes", { type: "manual", message: "Please select at least 1 freight mode" });
      return;
    }

    const payload = createPayload(data, freightModesArray);
    if (selectedLocationReferenceNumber) updatePaymentLocationMutation(payload);
    else createPaymentLocationMutation(payload);
  };

  return { handleSubmitPaymentLocation, isLoading: isCreating || isUpdating };
}
